// D-075 Move your store to Adyvance (owner, 2026-10-27, after Zalient's "Migrate" app): the Apps page
// banner and its popup, where the whole move happens (pick the platform, connect, import, import
// again, disconnect), and the connect panel Move your store also shows. Shopify, Blanxer and Zalient
// connect directly (the server reads them in the background and imports through the same importer as
// a spreadsheet); Pathao Commerce is shown as coming soon until its connection is verified.
// The platforms' official logos are used with the owner's permission (2026-10-27; D-075).
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatRelative } from "@ady/format";
import { t } from "@ady/i18n";
import { Badge, Banner, cx, Dialog, Icon, Popup, useToast, type IconName } from "@ady/ui";
import { ApiError } from "../../api/client";
import { errorMessage } from "../../api/errors";
import { HeadButton } from "../../finance/ui";
import { PopupHead } from "../../finance/MoneyPopups";
import { Field, frame } from "../../marketing/ui";
import { AppLink } from "../../shell/AppLink";
import { useShell } from "../../shell/context";
import { findItem, hrefFor } from "../../shell/nav";
import blanxerLogo from "./assets/migrate/blanxer.png";
import pathaoLogo from "./assets/migrate/pathao-commerce.svg";
import shopifyLogo from "./assets/migrate/shopify.png";
import zalientLogo from "./assets/migrate/zalient.svg";
import { useStoreIds } from "./common";
import {
  MIGRATION_SOURCES,
  migrationKeys,
  migrationsApi,
  SOURCE_KINDS,
  useMigrations,
  type MigrateKind,
  type Migration,
  type MigrationJob,
  type MigrationSource,
} from "./migrations";

export const isDirect = (s: string): s is MigrationSource =>
  (MIGRATION_SOURCES as readonly string[]).includes(s);

/** Official logos, downloaded from each brand's own site (D-075 lists the sources). */
const LOGO: Record<MigrationSource, string> = {
  shopify: shopifyLogo,
  blanxer: blanxerLogo,
  zalient: zalientLogo,
  pathao: pathaoLogo,
};

/** The platform's logo on a white tile; other platforms get a plain icon. */
export function SourceMark({ source, size = "md" }: { source: string; size?: "sm" | "md" }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        // Logos are made for white, in dark mode too: a dark tile hid Zalient's black mark (c7).
        "flex flex-none items-center justify-center overflow-hidden rounded-xs border border-card bg-artboard",
        size === "sm" ? "size-6 p-0.5" : "size-10 p-1.5",
      )}
    >
      {isDirect(source) ? (
        <img src={LOGO[source]} alt="" className="size-full object-contain" />
      ) : (
        <Icon name="table" className="text-link" />
      )}
    </span>
  );
}

// ------------------------------------------------------------------ Apps page banner + popup

export function MigrateBanner() {
  const [open, setOpen] = useState(false);
  // A platform chip opens the popup straight on that platform (owner, 2026-11-05).
  const [start, setStart] = useState<MigrationSource | null>(null);
  const openAt = (s: MigrationSource | null) => {
    setStart(s);
    setOpen(true);
  };
  return (
    <section
      className="ady-bg-brand-tint flex flex-wrap items-center gap-3 rounded-card border border-card p-4"
      data-testid="migrate-banner"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex items-center gap-2">
          <span className="text-base font-semibold">
            {t("storefront.admin.migrate.bannerTitle")}
          </span>
          <Badge tone="action">{t("storefront.admin.migrate.new")}</Badge>
        </span>
        <p className="max-w-2xl text-sm text-fg-secondary">
          {t("storefront.admin.migrate.bannerBody")}
        </p>
        <span className="flex flex-wrap gap-1.5 pt-0.5">
          {MIGRATION_SOURCES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => openAt(s)}
              className="flex items-center gap-1.5 rounded-full border border-card bg-surface py-0.5 ps-0.5 pe-2.5 text-xs font-medium hover:bg-hover"
              data-testid={`migrate-chip-${s}`}
            >
              <SourceMark source={s} size="sm" />
              {t(`storefront.admin.migrate.sources.${s}.name`)}
            </button>
          ))}
        </span>
      </div>
      <HeadButton
        icon="swap"
        primary
        label={t("storefront.admin.migrate.migrate")}
        onClick={() => openAt(null)}
        testId="migrate-open"
      />
      <MigratePopup open={open} start={start} onClose={() => setOpen(false)} />
    </section>
  );
}

/** The whole move in one popup: pick the platform, then connect and import without leaving it. */
export function MigratePopup({
  open,
  start = null,
  onClose,
}: {
  open: boolean;
  /** Open straight on this platform (a banner chip); null shows the platform list. */
  start?: MigrationSource | null;
  onClose: () => void;
}) {
  const [source, setSource] = useState<MigrationSource | null>(start);
  useEffect(() => {
    setSource(open ? start : null);
  }, [open, start]);
  return (
    <Popup open={open} onClose={onClose} label={t("storefront.admin.migrate.bannerTitle")}>
      <div className="flex flex-col gap-3" data-testid="migrate-popup">
        {source ? (
          <>
            <div className="flex items-center gap-2 border-b border-subtle pb-2.5">
              <button
                type="button"
                onClick={() => setSource(null)}
                title={t("storefront.admin.migrate.back")}
                className="inline-flex size-control flex-none items-center justify-center rounded-xs border border-card text-fg-secondary hover:bg-hover"
              >
                <Icon name="chevronLeft" />
                <span className="ady-sr-only">{t("storefront.admin.migrate.back")}</span>
              </button>
              <SourceMark source={source} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-base font-semibold">
                  {t("storefront.admin.migrate.fromTitle", {
                    name: t(`storefront.admin.migrate.sources.${source}.name`),
                  })}
                </span>
                <span className="text-xs text-fg-tertiary">
                  {t(`storefront.admin.migrate.sources.${source}.brings`)}
                </span>
              </span>
              <button
                type="button"
                onClick={onClose}
                title={t("storefront.admin.migrate.close")}
                className="inline-flex size-control flex-none items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
              >
                <Icon name="x" />
                <span className="ady-sr-only">{t("storefront.admin.migrate.close")}</span>
              </button>
            </div>
            <ConnectPanel source={source} onLeave={onClose} />
          </>
        ) : (
          <>
            <PopupHead
              icon="swap"
              title={t("storefront.admin.migrate.bannerTitle")}
              onClose={onClose}
            />
            <p className="text-sm text-fg-secondary">{t("storefront.admin.migrate.where")}</p>
            <PickList onPick={setSource} />
            <p className="text-2xs text-fg-tertiary">{t("storefront.admin.migrate.again")}</p>
          </>
        )}
      </div>
    </Popup>
  );
}

function PickList({ onPick }: { onPick: (s: MigrationSource) => void }) {
  const { orgId, storeId } = useStoreIds();
  const q = useMigrations(orgId, storeId);
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {MIGRATION_SOURCES.map((s) => {
        const m = q.data?.data.find((x) => x.source === s);
        return (
          <li key={s}>
            <button
              type="button"
              onClick={() => onPick(s)}
              className="ady-hover-brand flex h-full w-full items-center gap-3 rounded-sm border border-card bg-surface p-3 text-start"
              data-testid={`migrate-from-${s}`}
            >
              <SourceMark source={s} />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="ady-hover-label text-sm font-semibold">
                    {t(`storefront.admin.migrate.sources.${s}.name`)}
                  </span>
                  {m ? (
                    <Badge
                      tone={
                        m.status === "failed"
                          ? "danger"
                          : m.status === "running"
                            ? "action"
                            : "success"
                      }
                    >
                      {t(`storefront.admin.migrate.status.${m.status}`)}
                    </Badge>
                  ) : (
                    <Badge tone="success">{t("storefront.admin.migrate.direct")}</Badge>
                  )}
                </span>
                <span className="text-xs text-fg-tertiary">
                  {t(`storefront.admin.migrate.sources.${s}.brings`)}
                </span>
              </span>
              <Icon name="chevronRight" className="text-fg-tertiary" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ------------------------------------------------------------------ connect panel

/** How the store connects: Shopify's app kinds, or Blanxer's key or address (owner, 2026-11-05). */
type Method = "client" | "token" | "key" | "address";

export function ConnectPanel({
  source,
  onLeave,
}: {
  source: MigrationSource;
  /** Called before a link leaves the popup for another page. */
  onLeave?: () => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const q = useMigrations(orgId, storeId);
  const m = q.data?.data.find((x) => x.source === source);
  return (
    <div
      className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_19rem]"
      data-testid={`connect-${source}`}
    >
      <div className="flex min-w-0 flex-col gap-3">
        {m ? <Connected m={m} onLeave={onLeave} /> : <ConnectForm source={source} />}
        <Imported source={source} />
      </div>
      <Guide source={source} />
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: IconName; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5 rounded-card border border-card bg-surface p-4 shadow-card">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <Icon name={icon} gradient />
        {title}
      </h3>
      {children}
    </section>
  );
}

function ConnectForm({ source }: { source: MigrationSource }) {
  const { orgId, storeId } = useStoreIds();
  const qc = useQueryClient();
  const toast = useToast();
  const [method, setMethod] = useState<Method>(source === "blanxer" ? "key" : "client");
  const [v, setV] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Shown in the card: the popup is a modal dialog, which covers the toast region.
  const [failed, setFailed] = useState<string | null>(null);
  const pick = (m: Method) => {
    setMethod(m);
    setErrors({});
    setFailed(null);
  };
  const set = (k: string) => (e: { target: { value: string } }) => {
    setV({ ...v, [k]: e.target.value });
    setFailed(null);
  };
  const fields: { key: string; secret?: boolean; placeholder?: string; optional?: boolean }[] =
    source === "shopify"
      ? [
          { key: "address", placeholder: "your-store.myshopify.com" },
          ...(method === "client"
            ? [{ key: "client_id" }, { key: "client_secret", secret: true }]
            : [{ key: "access_token", secret: true, placeholder: "shpat_…" }]),
        ]
      : source === "blanxer"
        ? method === "key"
          ? [
              { key: "api_key", secret: true, placeholder: "sk_…" },
              { key: "address", placeholder: "yourstore.blanxer.io", optional: true },
            ]
          : [{ key: "address", placeholder: "yourstore.blanxer.io" }]
        : source === "pathao"
          ? [{ key: "address", placeholder: "yourshop.pathao.shop" }]
          : [{ key: "api_key", secret: true, placeholder: "zk_live_…" }];
  const ready = fields.every((f) => f.optional || (v[f.key] ?? "").trim() !== "");
  const connect = useMutation({
    mutationFn: () =>
      migrationsApi.connect(
        orgId,
        storeId,
        source,
        Object.fromEntries(
          fields.map((f) => [f.key, (v[f.key] ?? "").trim()] as const).filter(([, x]) => x !== ""),
        ),
      ),
    onSuccess: async () => {
      setErrors({});
      setFailed(null);
      toast.show({ tone: "success", title: t("storefront.admin.migrate.connected") });
      await qc.invalidateQueries({ queryKey: migrationKeys.list(orgId, storeId) });
    },
    onError: (e) => {
      setErrors(e instanceof ApiError && e.fields ? e.fields : {});
      setFailed(connectError(e, t(`storefront.admin.migrate.sources.${source}.name`)));
    },
  });
  return (
    <Card
      icon="link"
      title={t("storefront.admin.migrate.connectTitle", {
        name: t(`storefront.admin.migrate.sources.${source}.name`),
      })}
    >
      <p className="text-xs text-fg-secondary">
        {source === "blanxer" && method === "address"
          ? t("storefront.admin.migrate.sources.blanxer.leadAddress")
          : t(`storefront.admin.migrate.sources.${source}.lead`)}
      </p>
      {source === "blanxer" ? (
        <MethodChoice
          label={t("storefront.admin.migrate.fields.blanxerMethod")}
          value={method}
          onChange={pick}
          items={["key", "address"]}
        />
      ) : null}
      <form
        className="flex flex-col gap-2.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) connect.mutate();
        }}
      >
        {fields.map((f, i) => (
          <Fragment key={f.key}>
            <Field
              label={t(`storefront.admin.migrate.fields.${f.key as "address"}`)}
              error={errors[f.key] ? t("storefront.admin.migrate.checkField") : undefined}
              help={
                f.key === "address"
                  ? f.optional
                    ? t("storefront.admin.migrate.sources.blanxer.addressOptional")
                    : t(`storefront.admin.migrate.sources.${source}.addressHelp`)
                  : undefined
              }
            >
              <input
                className={cx(frame, f.secret && "font-mono")}
                type={f.secret ? "password" : "text"}
                autoComplete="off"
                spellCheck={false}
                placeholder={f.placeholder}
                value={v[f.key] ?? ""}
                onChange={set(f.key)}
                data-testid={`migrate-${f.key}`}
              />
            </Field>
            {/* Shopify: the store address first, then how its app was made (owner, 2026-11-05). */}
            {source === "shopify" && i === 0 ? (
              <MethodChoice
                label={t("storefront.admin.migrate.fields.method")}
                value={method}
                onChange={pick}
                items={["client", "token"]}
              />
            ) : null}
          </Fragment>
        ))}
        <p className="flex items-start gap-1.5 text-2xs text-fg-tertiary">
          <Icon name="lock" />
          {source === "blanxer" && method === "address"
            ? t("storefront.admin.migrate.sources.blanxer.safeAddress")
            : t(`storefront.admin.migrate.sources.${source}.safe`)}
        </p>
        {failed ? <Banner kind="error" title={failed} /> : null}
        <span>
          <button
            type="submit"
            disabled={!ready || connect.isPending}
            className="ady-bg-brand inline-flex min-h-control items-center gap-1.5 rounded-xs px-3 text-sm font-medium text-on-primary disabled:opacity-45"
            data-testid="migrate-connect"
          >
            <Icon name="link" />
            {connect.isPending
              ? t("storefront.admin.migrate.checking")
              : t("storefront.admin.migrate.connect")}
          </button>
        </span>
      </form>
    </Card>
  );
}

/** Why connecting failed, in words the merchant can act on (the server's codes, D-075). */
function connectError(e: unknown, name: string): string {
  if (e instanceof ApiError) {
    if (e.code === "import.source_refused") {
      // Show what the platform itself answered (owner, 2026-11-05: "then I see what happens").
      const status = e.details?.status;
      const said = typeof e.details?.detail === "string" ? e.details.detail : "";
      return typeof status === "number"
        ? t("storefront.admin.migrate.errors.refusedStatus", {
            name,
            status,
            said: said ? `: ${said}` : "",
          })
        : t("storefront.admin.migrate.errors.refused", { name });
    }
    if (e.code.startsWith("validation.") && e.fields?.address === "choose")
      return t("storefront.admin.migrate.errors.chooseStore", { name });
    if (e.code === "import.source_unavailable")
      return t("storefront.admin.migrate.errors.unavailable", { name });
    if (e.code.startsWith("validation.") && e.fields?.api_key)
      return t("storefront.admin.migrate.errors.apiKey", { name });
    if (e.code.startsWith("validation.") && e.fields?.address)
      return t("storefront.admin.migrate.errors.address");
  }
  return errorMessage(e);
}

/** How the store connects, as two cards with a one-line hint each (owner, 2026-11-05). */
function MethodChoice({
  label,
  value,
  onChange,
  items,
}: {
  label: string;
  value: Method;
  onChange: (m: Method) => void;
  items: Method[];
}) {
  return (
    // Not a <label> (Field): two buttons inside one label both answer a click on it.
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((id) => {
          const on = value === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(id)}
              className={cx(
                "flex flex-col items-start gap-0.5 rounded-xs border-[1.5px] p-2.5 text-start",
                on ? "ady-outline-brand" : "border-card bg-surface hover:bg-hover",
              )}
              data-testid={`migrate-method-${id}`}
            >
              <span className={cx("text-sm font-semibold", on && "text-link")}>
                {t(`storefront.admin.migrate.methods.${id}`)}
              </span>
              <span className="text-2xs text-fg-tertiary">
                {t(`storefront.admin.migrate.methods.${id}Hint`)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const STATE_TONE: Record<string, "success" | "warning" | "danger" | "neutral" | "action"> = {
  done: "success",
  running: "action",
  dry_run: "action",
  failed: "danger",
  cancelled: "neutral",
};

function Connected({ m, onLeave }: { m: Migration; onLeave?: () => void }) {
  const { orgId, storeId } = useStoreIds();
  const { orgSlug, storeSlug } = useShell();
  const qc = useQueryClient();
  const toast = useToast();
  // Blanxer: what the connection can bring (customers only with the key); others: the platform's.
  const kindsAvailable =
    m.source === "blanxer" && m.kinds.length ? m.kinds : SOURCE_KINDS[m.source];
  const [kinds, setKinds] = useState<MigrateKind[]>(kindsAvailable);
  const [confirm, setConfirm] = useState(false);
  // In the card, not a toast: the popup's modal dialog covers the toast region.
  const [failed, setFailed] = useState<string | null>(null);
  const name = t(`storefront.admin.migrate.sources.${m.source}.name`);
  const refresh = () => qc.invalidateQueries({ queryKey: migrationKeys.list(orgId, storeId) });
  const run = useMutation({
    mutationFn: () => migrationsApi.run(orgId, storeId, m.source, kinds),
    onSuccess: async () => {
      setFailed(null);
      toast.show({ tone: "success", title: t("storefront.admin.migrate.started") });
      await refresh();
    },
    onError: (e) => setFailed(connectError(e, name)),
  });
  const disconnect = useMutation({
    mutationFn: () => migrationsApi.disconnect(orgId, storeId, m.source),
    onSuccess: async () => {
      setConfirm(false);
      toast.show({ tone: "success", title: t("storefront.admin.migrate.disconnected") });
      await refresh();
    },
    onError: (e) => {
      setConfirm(false);
      setFailed(connectError(e, name));
    },
  });
  const running = m.status === "running";
  const ran = Object.keys(m.jobs).length > 0;
  const importer = findItem("import");
  return (
    <Card icon="checkCircle" title={t("storefront.admin.migrate.connectedTitle")}>
      <div className="flex flex-wrap items-center gap-3 rounded-sm border border-card p-3">
        <SourceMark source={m.source} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold" data-content="user">
            {m.account.name}
          </span>
          <span className="flex flex-wrap items-center gap-x-2 text-2xs text-fg-tertiary">
            {m.account.url ? (
              <a
                href={m.account.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-link"
              >
                {m.account.url.replace(/^https:\/\//, "")}
              </a>
            ) : null}
            {m.connected_at
              ? t("storefront.admin.migrate.connectedWhen", {
                  when: formatRelative(m.connected_at),
                })
              : null}
          </span>
        </span>
        <Badge
          tone={
            m.status === "failed"
              ? "danger"
              : m.status === "done"
                ? "success"
                : running
                  ? "action"
                  : "neutral"
          }
        >
          {t(`storefront.admin.migrate.status.${m.status}`)}
        </Badge>
      </div>
      {m.last_error ? <Banner kind="error" title={m.last_error} /> : null}
      {failed ? <Banner kind="error" title={failed} /> : null}
      <fieldset className="flex flex-col gap-1.5" disabled={running}>
        <legend className="pb-1 text-xs font-medium text-fg-secondary">
          {t("storefront.admin.migrate.bring")}
        </legend>
        {kindsAvailable.map((k) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={kinds.includes(k)}
              onChange={(e) =>
                setKinds(e.target.checked ? [...kinds, k] : kinds.filter((x) => x !== k))
              }
            />
            <span>{t(`storefront.admin.migrate.kinds.${k}`)}</span>
            <span className="text-2xs text-fg-tertiary">
              {t(`storefront.admin.migrate.kinds.${k}Help`)}
            </span>
          </label>
        ))}
      </fieldset>
      {ran ? (
        <ul className="flex flex-col gap-2" data-testid="migrate-jobs">
          {(Object.entries(m.jobs) as [MigrateKind, MigrationJob][]).map(([kind, j]) => (
            <JobRow
              key={kind}
              kind={kind}
              job={j}
              reading={m.progress?.kind === kind ? m.progress.read : null}
            />
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <HeadButton
          icon={ran ? "refresh" : "download"}
          primary
          label={
            running
              ? t("storefront.admin.migrate.importing")
              : ran
                ? t("storefront.admin.migrate.runAgain")
                : t("storefront.admin.migrate.run")
          }
          onClick={() => run.mutate()}
          disabled={running || run.isPending || kinds.length === 0}
          testId="migrate-run"
        />
        {ran && importer ? (
          <AppLink
            href={hrefFor(importer, orgSlug, storeSlug)}
            onClick={onLeave}
            className="text-xs font-medium text-link"
          >
            {t("storefront.admin.migrate.details")}
          </AppLink>
        ) : null}
        <span className="flex-1" />
        <button
          type="button"
          disabled={running}
          onClick={() => setConfirm(true)}
          className="rounded-xs px-2 text-xs font-medium text-fg-secondary hover:bg-hover hover:text-danger-fg disabled:opacity-45"
        >
          {t("storefront.admin.migrate.disconnect")}
        </button>
      </div>
      <p className="text-2xs text-fg-tertiary">{t("storefront.admin.migrate.again")}</p>
      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t("storefront.admin.migrate.disconnectTitle", {
          name: t(`storefront.admin.migrate.sources.${m.source}.name`),
        })}
        description={t("storefront.admin.migrate.disconnectBody")}
        confirm={{
          label: t("storefront.admin.migrate.disconnect"),
          loading: disconnect.isPending,
          onClick: () => disconnect.mutate(),
          variant: "danger",
        }}
      />
    </Card>
  );
}

function JobRow({
  kind,
  job,
  reading,
}: {
  kind: MigrateKind;
  job: MigrationJob;
  reading: number | null;
}) {
  const done = job.state === "done";
  const pct = done
    ? 100
    : job.chunks.total
      ? Math.round((job.chunks.done / job.chunks.total) * 100)
      : reading
        ? 15
        : 5;
  return (
    <li className="flex flex-col gap-1.5 rounded-sm border border-card p-2.5">
      <span className="flex items-center gap-2 text-xs">
        <Icon
          name={kind === "products" ? "box" : kind === "orders" ? "cart" : "users"}
          className="text-link"
        />
        <span className="flex-1 font-semibold">{t(`storefront.admin.migrate.kinds.${kind}`)}</span>
        <Badge tone={STATE_TONE[job.state] ?? "neutral"}>
          {reading !== null
            ? t("storefront.admin.migrate.reading", { n: reading })
            : t(
                `storefront.admin.migrate.jobState.${(job.state in STATE_TONE ? job.state : "running") as "done"}`,
              )}
        </Badge>
      </span>
      <span className="h-1.5 overflow-hidden rounded-full bg-sunken" aria-hidden="true">
        <span
          className="ady-bg-brand block h-full rounded-full transition-[width]"
          ref={(el) => el?.style.setProperty("width", `${pct}%`)}
        />
      </span>
      {done || job.rows.ok || job.rows.failed || job.rows.skipped ? (
        <span className="flex flex-wrap gap-x-3 text-2xs text-fg-secondary">
          <span>{t("storefront.admin.migrate.counts.ok", { n: job.rows.ok })}</span>
          {job.rows.skipped ? (
            <span>{t("storefront.admin.migrate.counts.skipped", { n: job.rows.skipped })}</span>
          ) : null}
          {job.rows.failed ? (
            <span className="text-danger-fg">
              {t("storefront.admin.migrate.counts.failed", { n: job.rows.failed })}
            </span>
          ) : null}
          {kind === "products" && job.images.ok ? (
            <span>{t("storefront.admin.migrate.counts.photos", { n: job.images.ok })}</span>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

/** What comes across from the platform, and what does not (yet). */
function Imported({ source }: { source: MigrationSource }) {
  const items = t(`storefront.admin.migrate.sources.${source}.what`).split("|");
  const not = t(`storefront.admin.migrate.sources.${source}.not`);
  return (
    <Card icon="layers" title={t("storefront.admin.migrate.whatTitle")}>
      <ul className="flex flex-col gap-1">
        {items.map((x) => (
          <li key={x} className="flex items-start gap-2 text-xs">
            <span className="pt-px text-success-fg">
              <Icon name="check" />
            </span>
            <span>{x}</span>
          </li>
        ))}
      </ul>
      {not ? <p className="text-2xs text-fg-tertiary">{not}</p> : null}
    </Card>
  );
}

function Guide({ source }: { source: MigrationSource }) {
  const steps = t(`storefront.admin.migrate.sources.${source}.steps`).split("|");
  // Shopify: the access scopes to add, each with what it brings (owner, 2026-11-05).
  const scopes =
    source === "shopify"
      ? t("storefront.admin.migrate.sources.shopify.scopes")
          .split("|")
          .map((x) => x.split("="))
      : [];
  return (
    <aside className="flex h-fit flex-col gap-2.5 rounded-card border border-card bg-surface p-4 shadow-card">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <Icon name="help" gradient />
        {t(`storefront.admin.migrate.sources.${source}.guideTitle`)}
      </h3>
      <ol className="flex flex-col gap-2">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="flex size-5 flex-none items-center justify-center rounded-full bg-sunken text-2xs font-bold text-fg-secondary">
              {i + 1}
            </span>
            <span className="flex min-w-0 flex-col gap-1.5 text-fg-secondary">
              {s}
              {source === "shopify" && i === 0 ? (
                <a
                  href="https://dev.shopify.com/dashboard/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-medium text-link"
                >
                  {t("storefront.admin.migrate.sources.shopify.open")}
                  <Icon name="external" />
                </a>
              ) : null}
              {source === "shopify" && i === 1 ? (
                <span
                  className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-2 gap-y-1"
                  data-testid="migrate-scopes"
                >
                  {scopes.map(([scope, what]) => (
                    <Fragment key={scope}>
                      <code className="rounded-xs bg-sunken px-1.5 font-mono text-2xs text-fg">
                        {scope}
                      </code>
                      <span className="text-2xs">{what}</span>
                    </Fragment>
                  ))}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </aside>
  );
}
